import type { ReactNode } from 'react'

import { CaptureOutcomeBadge } from '@/components/jobs/CaptureList'
import { StatusBadge } from '@/components/judgments/StatusBadge'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingPanel } from '@/components/ui/Spinner'
import { useJudgmentDetail } from '@/hooks/useJudgments'
import { useToast } from '@/hooks/useToast'
import { copyToClipboard, downloadText, timestampedName } from '@/lib/download'
import { EMPTY, formatDateTime, formatMoney, titleCase } from '@/lib/format'
import type { JudgmentDetail } from '@/types/api'

export interface JudgmentDetailDrawerProps {
  judgmentId: number | null
  onClose: () => void
}

/**
 * A label/value pair.
 *
 * Two columns from `sm` up, stacked below it: at 360px a fixed 9rem label
 * column left the values about 12 characters of space, so docket numbers and
 * case titles wrapped mid-word.
 */
function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid gap-0.5 py-1.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs text-ink-muted sm:pt-0.5">{label}</dt>
      <dd className="text-base break-words text-ink">{value || EMPTY}</dd>
    </div>
  )
}

function Section({
  title,
  children,
  note,
}: {
  title: string
  children: ReactNode
  note?: string
}) {
  return (
    <section className="border-b border-line px-4 py-4 last:border-b-0">
      <h3 className="mb-2 text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase">
        {title}
      </h3>
      {note ? <p className="mb-2 text-xs text-ink-secondary">{note}</p> : null}
      {children}
    </section>
  )
}

function NameList({ names, empty }: { names: string[]; empty: string }) {
  if (names.length === 0) return <p className="text-sm text-ink-muted">{empty}</p>
  return (
    <ul className="flex flex-col gap-1">
      {names.map((name) => (
        <li key={name} className="text-base text-ink">
          {name}
        </li>
      ))}
    </ul>
  )
}

const CopyIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M10.5 5.5v-1a1.5 1.5 0 0 0-1.5-1.5H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 11h1"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
)

const DownloadIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
    <path
      d="M8 2.5v7.5M5 7.5L8 10.5l3-3M3 12.5h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export function JudgmentDetailDrawer({
  judgmentId,
  onClose,
}: JudgmentDetailDrawerProps) {
  const { data, isLoading, isError, error, refetch } = useJudgmentDetail(judgmentId)
  const { push } = useToast()

  const onCopy = async (judgment: JudgmentDetail) => {
    const ok = await copyToClipboard(JSON.stringify(judgment, null, 2))
    push({
      tone: ok ? 'success' : 'error',
      title: ok ? 'Copied to clipboard' : 'Could not copy',
      description: ok ? undefined : 'The browser refused clipboard access.',
    })
  }

  return (
    <Drawer
      open={judgmentId !== null}
      onClose={onClose}
      title={data?.judgmentNumber || 'Judgment'}
      subtitle={data?.caseTitle}
      footer={
        data ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" icon={<CopyIcon />} onClick={() => void onCopy(data)}>
              Copy JSON
            </Button>
            <Button
              size="sm"
              icon={<DownloadIcon />}
              onClick={() =>
                downloadText(
                  JSON.stringify(data, null, 2),
                  timestampedName(
                    `judgment_${data.judgmentNumber.replace(/\s+/g, '_') || data.id}`,
                    'json',
                  ),
                  'application/json',
                )
              }
            >
              Download JSON
            </Button>
          </div>
        ) : undefined
      }
    >
      {isLoading ? <LoadingPanel label="Loading judgment…" /> : null}

      {isError ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this judgment"
          onRetry={() => void refetch()}
        />
      ) : null}

      {data ? (
        <>
          <Section title="Judgment">
            <dl>
              <DetailRow
                label="Judgment #"
                value={<span className="ident">{data.judgmentNumber}</span>}
              />
              <DetailRow
                label="Docket #"
                value={data.docketNumber ? <span className="ident">{data.docketNumber}</span> : ''}
              />
              <DetailRow
                label="Date entered"
                value={data.dateEntered ? <span className="ident">{data.dateEntered}</span> : ''}
              />
              <DetailRow
                label="Amount"
                value={
                  <span className="ident font-medium">
                    {data.amountValue ? formatMoney(data.amountValue) : data.amountText}
                  </span>
                }
              />
              <div className="grid gap-1 py-1.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center sm:gap-3">
                <dt className="text-xs text-ink-muted">Status</dt>
                <dd>
                  <StatusBadge status={data.status} statusText={data.statusText} />
                </dd>
              </div>
              <DetailRow label="Case title" value={data.caseTitle} />
              <DetailRow label="Filing location" value={data.filingLocation} />
            </dl>
          </Section>

          <Section title="Debtors">
            <NameList names={data.debtorNames} empty="No debtor recorded." />
          </Section>

          <Section
            title="Creditors"
            note={
              data.creditorNames.length === 0 && !data.detailFetched
                ? 'The creditor appears only on the portal’s detail page. Re-run this capture with “fetch details” to collect it.'
                : undefined
            }
          >
            <NameList names={data.creditorNames} empty="No creditor recorded." />
          </Section>

          {data.parties.length > 0 ? (
            <Section
              title="Party / debt summary"
              note="A judgment can carry several debts and several parties."
            >
              <div className="scrollbar-slim overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[24rem] text-sm">
                  <thead className="bg-surface-sunken">
                    <tr>
                      {['Name', 'Role', 'Status', 'Debt #'].map((heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="px-2.5 py-2 text-left text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.parties.map((party) => (
                      <tr key={party.id} className="border-t border-line-subtle">
                        <td className="px-2.5 py-2 text-ink">{party.name}</td>
                        <td className="px-2.5 py-2 text-ink-secondary">
                          {party.roleText || titleCase(party.role)}
                        </td>
                        <td className="px-2.5 py-2 text-ink-secondary">
                          {party.statusText || EMPTY}
                        </td>
                        <td className="tabular px-2.5 py-2 text-ink-secondary">
                          {party.debtSequence || EMPTY}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}

          {Object.keys(data.detailFields).length > 0 ? (
            <Section
              title="Detail page fields"
              note="Every label/value pair the portal’s detail page exposed."
            >
              <dl>
                {Object.entries(data.detailFields).map(([label, value]) => (
                  <DetailRow key={label} label={titleCase(label)} value={value} />
                ))}
              </dl>
            </Section>
          ) : null}

          <Section
            title="Provenance"
            note="Which ingested searches reported this judgment."
          >
            <div className="flex flex-col gap-2">
              {data.captures.map((capture) => (
                <div
                  key={capture.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-surface-sunken px-2.5 py-2"
                >
                  <span className="min-w-0 truncate text-sm text-ink">{capture.label}</span>
                  <CaptureOutcomeBadge capture={capture} />
                </div>
              ))}
              <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
                <div className="flex gap-1">
                  <dt>Matched as {titleCase(data.matchedPartyRole)}:</dt>
                  <dd className="text-ink-secondary">{data.matchedName}</dd>
                </div>
                <div className="flex gap-1">
                  <dt>Detail page read:</dt>
                  <dd className="text-ink-secondary">{data.detailFetched ? 'yes' : 'no'}</dd>
                </div>
                <div className="flex gap-1">
                  <dt>First seen</dt>
                  <dd className="text-ink-secondary">{formatDateTime(data.createdAt)}</dd>
                </div>
              </dl>
            </div>
          </Section>
        </>
      ) : null}
    </Drawer>
  )
}
